import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PROJECTS, SERVICES } from "@/data/mock";
const FILTERS = [{ slug: "all", title: "All" }, ...SERVICES.map((s) => ({ slug: s.slug, title: s.title }))];

export default function Projects() {
  const [active, setActive] = useState("all");
  const filtered = useMemo(
    () => (active === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === active)),
    [active]
  );

  return (
    <div data-testid="projects-page" className="bg-brand-bgSageSoft">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
        <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
          Selected projects
        </p>
        <h1 className="mt-4 font-display text-5xl md:text-7xl font-semibold tracking-tighter leading-[0.95] text-brand-ink max-w-4xl text-balance">
          Real builds. Real streets. Walk past most of them.
        </h1>
        <p className="mt-6 text-lg text-brand-inkSoft max-w-2xl">
          A small slice of recent work across Berkshire. Happy to share addresses on
          request — most clients are glad to have you over.
        </p>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-6">
        <div
          className="flex flex-wrap gap-2 border-b border-brand-line pb-6"
          data-testid="projects-filters"
        >
          {FILTERS.map((f, i) => {
            const activeColor = [
              "bg-brand-ink",
              "bg-brand-terracotta",
              "bg-brand-sageDark",
              "bg-brand-ochreDark",
              "bg-brand-terracottaDark",
              "bg-brand-sage"
            ][i % 6];
            return (
              <button
                key={f.slug}
                data-testid={`projects-filter-${f.slug}`}
                onClick={() => setActive(f.slug)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === f.slug
                    ? `${activeColor} text-white`
                    : "bg-brand-bgAlt text-brand-inkSoft hover:text-brand-ink"
                }`}
              >
                {f.title}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {filtered.map((p) => (
            <Link
              to={`/projects/${p.id}`}
              key={p.id}
              data-testid={`project-card-${p.id}`}
              className="group rounded-3xl overflow-hidden border border-brand-line bg-white hover:-translate-y-1 transition-transform"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                />
              </div>
              <div className="p-6 flex items-start justify-between gap-3">
                <h3 className="font-display text-2xl font-medium tracking-tight text-brand-ink">
                  {p.title}
                </h3>
                <span className="shrink-0 mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-terracotta text-white transition-transform group-hover:-translate-y-0.5">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-brand-inkSoft text-center py-20" data-testid="projects-empty">
            No projects in this category yet — but plenty in the pipeline.
          </p>
        )}
      </section>

      <section className="bg-brand-ink text-brand-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight max-w-xl">
            Got a project in mind? Let’s have a proper chat.
          </h2>
          <Button
            asChild
            data-testid="projects-cta"
            className="rounded-full h-12 px-7 bg-brand-terracotta hover:bg-brand-terracottaDark text-white self-start md:self-auto"
          >
            <Link to="/contact">
              Request a quote <ArrowUpRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
